import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { Answer, AnswerRequest, Form } from "../dynamicForm";
import { trustAPI } from "./trustBaseApiClient";
import { queryClient } from "../api/queryClient";

const trustKeysAll = () => ["trust"] as const;

const trustKeys = {
  all: trustKeysAll,
  userInfo: () => [...trustKeysAll(), "user-info"] as const,
  assessmentsGridView: () => [...trustKeysAll(), "assessments", "gridview"] as const,
  assessmentsForm: () => [...trustKeysAll(), "assessments", "form"] as const,
  assessmentFormStandardAnswers: () => [...trustKeysAll(), "assessments", "standard-answers"] as const,
  assessment: (id: string) => [...trustKeysAll(), "assessments", id] as const,
} as const;

export const LanguageSchema = z.enum(["en", "sv", "no"]);
export type Language = z.infer<typeof LanguageSchema>;

const TrustUserInfoSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  jobTitle: z.string(),
  language: LanguageSchema,
  email: z.string(),
});

export type TrustUserInfo = z.infer<typeof TrustUserInfoSchema>;

const OrganisationSchema = z.object({ id: z.string(), name: z.string() });

const UserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
});

const RequestedBySchema = z.object({ user: UserSchema, organisation: OrganisationSchema });

const GridViewAssessmentSchema = z
  .object({
    id: z.string(),
    requestedBy: RequestedBySchema,
    requestedAt: z.string(),
  })
  .and(
    z.discriminatedUnion("status", [
      z.object({ status: z.literal("responded"), respondedAt: z.string() }),
      z.object({ status: z.literal("awaiting-response") }),
      z.object({ status: z.literal("withdrawn"), withdrawnAt: z.string() }),
    ])
  );

const AssessmentsGridViewResponseSchema = z.object({ assessments: z.array(GridViewAssessmentSchema) });
export type AssessmentsGridViewResponse = z.infer<typeof AssessmentsGridViewResponseSchema>;

const AssessmentFormResponseSchema = z.custom<Form>();

const AssessmentBaseSchema = z.object({
  id: z.string(),
  requestedAt: z.string(),
  requestedBy: RequestedBySchema,
});

const AwaitingResponseAssessmentSchema = AssessmentBaseSchema.extend({
  status: z.literal("awaiting-response"),
  customAnswers: z.custom<Array<Answer>>(),
});
export type AwaitingResponseAssessment = z.infer<typeof AwaitingResponseAssessmentSchema>;

const RespondedAssessmentSchema = AssessmentBaseSchema.extend({
  status: z.literal("responded"),
  respondedAt: z.string(),
  formSnapshot: z.custom<Form>(),
  answersSnapshot: z.custom<Array<Answer>>(),
});
export type RespondedAssessment = z.infer<typeof RespondedAssessmentSchema>;

const WithdrawnAssessmentDetailSchema = AssessmentBaseSchema.extend({
  status: z.literal("withdrawn"),
  withdrawnAt: z.string(),
});

const AssessmentDetailSchema = z.discriminatedUnion("status", [
  AwaitingResponseAssessmentSchema,
  RespondedAssessmentSchema,
  WithdrawnAssessmentDetailSchema,
]);

export const assessmentsGridViewQueryOptions = () =>
  queryOptions({
    queryKey: trustKeys.assessmentsGridView(),
    queryFn: () => trustAPI.get(`assessments/gridview`, { schema: AssessmentsGridViewResponseSchema }),
    throwOnError: true,
  });

export const assessmentQueryOptions = (id: string) =>
  queryOptions({
    queryKey: trustKeys.assessment(id),
    queryFn: () => trustAPI.get(`assessments/${id}`, { schema: AssessmentDetailSchema }),
    throwOnError: true,
  });

export const assessmentsFormQueryOptions = () =>
  queryOptions({
    queryKey: trustKeys.assessmentsForm(),
    queryFn: () => trustAPI.get(`assessments/form`, { schema: AssessmentFormResponseSchema }),
    throwOnError: true,
  });

const AssessmentFormStandardAnswersSchema = z.object({ answers: z.custom<Array<Answer>>() });
export const assessmentFormStandardAnswersQueryOptions = () =>
  queryOptions({
    queryKey: trustKeys.assessmentFormStandardAnswers(),
    queryFn: () => trustAPI.get(`assessments/form/standard-answers`, { schema: AssessmentFormStandardAnswersSchema }),
    throwOnError: true,
  });

export const updateStandardAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: Array<AnswerRequest>) =>
      trustAPI.patch(`assessments/form/standard-answers`, payload, {
        schema: AssessmentFormStandardAnswersSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const updateAssessmentAnswersMutationOptions = (assessmentId: string) =>
  mutationOptions({
    mutationFn: async (answers: AnswerRequest[]) => {
      await trustAPI.patch(`assessments/${assessmentId}/answers`, answers);
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const TrustUserInfoPayloadSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  jobTitle: z.string().min(1),
  language: LanguageSchema,
});

type TrustUserInfoPayload = z.infer<typeof TrustUserInfoPayloadSchema>;

export const trustUserInfoQueryOptions = () =>
  queryOptions({
    queryKey: trustKeys.userInfo(),
    queryFn: () => trustAPI.get(`user-info`, { schema: TrustUserInfoSchema }),
    throwOnError: true,
  });

export const updateTrustUserInfoMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: TrustUserInfoPayload) =>
      trustAPI.patch(`user-info`, payload, {
        schema: TrustUserInfoSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trustKeys.userInfo() }),
  });

export const signAssessmentMutationOptions = (assessmentId: string) =>
  mutationOptions({
    mutationFn: (signer: { firstName: string; lastName: string; email: string; jobTitle: string }) =>
      trustAPI.post(`assessments/${assessmentId}/sign`, { signer, updateStandardAnswers: false }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
